import React, { useState } from 'react'
import { NavLink, Outlet } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import {
  LayoutDashboard, Users, UserSearch, Package, FileText,
  Briefcase, UsersRound, BarChart3, LogOut, ChevronRight,
  Tent, Menu, X, BookOpen
} from 'lucide-react'

const adminNav = [
  { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
  { to: '/leads', icon: UserSearch, label: 'Leads' },
  { to: '/clients', icon: Users, label: 'Clients' },
  { to: '/inventory', icon: Package, label: 'Inventory' },
  { to: '/quotes', icon: FileText, label: 'Quotes' },
  { to: '/jobs', icon: Briefcase, label: 'Schedule' },
  { to: '/staff', icon: UsersRound, label: 'Staff' },
  { to: '/reports', icon: BarChart3, label: 'Reports' },
  { to: '/help', icon: BookOpen, label: 'Getting Started' },
]

const laborNav = [
  { to: '/my-jobs', icon: Briefcase, label: 'My Jobs' },
]

export default function AppLayout() {
  const { user, signOut, isAdmin, loading, profileError, effectiveRole } = useAuth()
  const [mobileOpen, setMobileOpen] = useState(false)

  if (loading || !user) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--color-text-muted)', padding: 24, textAlign: 'center' }}>
        {profileError ? `Unable to load your profile: ${profileError}` : 'Loading your profile…'}
      </div>
    )
  }

  if (!effectiveRole) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--color-text-muted)', padding: 24, textAlign: 'center' }}>
        Unable to determine your role from public.users. Please refresh or sign in again.
      </div>
    )
  }

  const navItems = isAdmin ? adminNav : laborNav

  const navContent = (
    <>
      {/* Logo */}
      <div className="sidebar-logo">
        <Tent size={22} className="logo-icon" />
        <div>
          <div className="logo-title">WTR</div>
          <div className="logo-sub">CRM System</div>
        </div>
      </div>

      {/* Nav links */}
      <nav className="sidebar-nav">
        {navItems.map(({ to, icon: Icon, label }) => (
          <NavLink
            key={to}
            to={to}
            className={({ isActive }) =>
              `sidebar-link ${isActive ? 'sidebar-link-active' : ''}`
            }
            onClick={() => setMobileOpen(false)}
          >
            <Icon size={17} />
            <span>{label}</span>
            <ChevronRight size={13} className="sidebar-link-arrow" />
          </NavLink>
        ))}
      </nav>

      {/* User */}
      <div className="sidebar-user">
        <div className="sidebar-user-info">
          <div className="sidebar-user-avatar">
            {user?.full_name?.charAt(0).toUpperCase() || '?'}
          </div>
          <div className="sidebar-user-details">
            <div className="sidebar-user-name">{user?.full_name}</div>
            <div className="sidebar-user-role">{user?.role}</div>
          </div>
        </div>
        <button className="sidebar-signout btn btn-ghost btn-sm" onClick={signOut} title="Sign out">
          <LogOut size={15} />
        </button>
      </div>
    </>
  )

  return (
    <div className="app-layout">
      {/* Mobile header */}
      <header className="mobile-header">
        <button className="btn btn-ghost" onClick={() => setMobileOpen(true)}>
          <Menu size={20} />
        </button>
        <div className="mobile-header-logo">
          <Tent size={18} />
          <span>WTR CRM</span>
        </div>
        <div style={{ width: 40 }} />
      </header>

      {/* Mobile sidebar overlay */}
      {mobileOpen && (
        <div className="mobile-overlay" onClick={() => setMobileOpen(false)}>
          <aside className="sidebar sidebar-mobile" onClick={e => e.stopPropagation()}>
            <button
              className="btn btn-ghost btn-sm mobile-close"
              onClick={() => setMobileOpen(false)}
            >
              <X size={18} />
            </button>
            {navContent}
          </aside>
        </div>
      )}

      {/* Desktop sidebar */}
      <aside className="sidebar sidebar-desktop">
        {navContent}
      </aside>

      {/* Main content */}
      <main className="main-content">
        <Outlet />
      </main>

      <style>{`
        .app-layout {
          display: flex;
          min-height: 100vh;
        }

        /* Sidebar */
        .sidebar {
          width: var(--sidebar-width);
          background: var(--color-navy);
          display: flex;
          flex-direction: column;
          padding: 0;
          flex-shrink: 0;
        }

        .sidebar-desktop {
          position: fixed;
          top: 0;
          left: 0;
          height: 100vh;
          z-index: 10;
        }

        .sidebar-logo {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 20px 18px 18px;
          border-bottom: 1px solid rgba(255,255,255,0.08);
        }

        .logo-icon {
          color: var(--color-green-light);
        }

        .logo-title {
          font-size: 16px;
          font-weight: 700;
          color: white;
          line-height: 1;
        }

        .logo-sub {
          font-size: 10px;
          color: rgba(255,255,255,0.45);
          text-transform: uppercase;
          letter-spacing: 0.08em;
          line-height: 1;
          margin-top: 3px;
        }

        .sidebar-nav {
          flex: 1;
          padding: 12px 10px;
          display: flex;
          flex-direction: column;
          gap: 2px;
          overflow-y: auto;
        }

        .sidebar-link {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 9px 10px;
          border-radius: var(--radius-sm);
          color: rgba(255,255,255,0.65);
          font-size: var(--text-sm);
          font-weight: 500;
          text-decoration: none;
          transition: all 0.12s ease;
          cursor: pointer;
        }

        .sidebar-link:hover {
          background: rgba(255,255,255,0.08);
          color: rgba(255,255,255,0.9);
        }

        .sidebar-link-active {
          background: rgba(42,157,92,0.2) !important;
          color: var(--color-green-light) !important;
        }

        .sidebar-link-arrow {
          margin-left: auto;
          opacity: 0;
          transition: opacity 0.12s;
        }

        .sidebar-link:hover .sidebar-link-arrow,
        .sidebar-link-active .sidebar-link-arrow {
          opacity: 0.5;
        }

        .sidebar-user {
          padding: 12px 14px;
          border-top: 1px solid rgba(255,255,255,0.08);
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 8px;
        }

        .sidebar-user-info {
          display: flex;
          align-items: center;
          gap: 10px;
          overflow: hidden;
        }

        .sidebar-user-avatar {
          width: 32px;
          height: 32px;
          border-radius: 50%;
          background: var(--color-green-dim);
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 13px;
          font-weight: 700;
          flex-shrink: 0;
        }

        .sidebar-user-details {
          overflow: hidden;
        }

        .sidebar-user-name {
          font-size: var(--text-sm);
          font-weight: 600;
          color: rgba(255,255,255,0.9);
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .sidebar-user-role {
          font-size: 10px;
          color: rgba(255,255,255,0.4);
          text-transform: uppercase;
          letter-spacing: 0.06em;
        }

        .sidebar-signout {
          color: rgba(255,255,255,0.35) !important;
          flex-shrink: 0;
        }

        .sidebar-signout:hover {
          color: rgba(255,255,255,0.7) !important;
          background: rgba(255,255,255,0.08) !important;
        }

        /* Main */
        .main-content {
          flex: 1;
          margin-left: var(--sidebar-width);
          padding: 28px 32px;
          min-width: 0;
        }

        /* Mobile */
        .mobile-header {
          display: none;
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          height: 56px;
          background: var(--color-navy);
          align-items: center;
          justify-content: space-between;
          padding: 0 16px;
          z-index: 20;
          border-bottom: 1px solid rgba(255,255,255,0.08);
        }

        .mobile-header .btn {
          color: rgba(255,255,255,0.7);
        }

        .mobile-header-logo {
          display: flex;
          align-items: center;
          gap: 8px;
          color: white;
          font-weight: 700;
          font-size: 15px;
        }

        .mobile-header-logo svg {
          color: var(--color-green-light);
        }

        .mobile-overlay {
          display: none;
          position: fixed;
          inset: 0;
          background: rgba(0,0,0,0.5);
          z-index: 50;
        }

        .sidebar-mobile {
          position: fixed;
          top: 0;
          left: 0;
          height: 100vh;
          z-index: 60;
          animation: slideRight 0.2s ease;
        }

        .mobile-close {
          position: absolute;
          top: 12px;
          right: 12px;
          color: rgba(255,255,255,0.5) !important;
        }

        @keyframes slideRight {
          from { transform: translateX(-100%); }
          to { transform: translateX(0); }
        }

        @media (max-width: 768px) {
          .sidebar-desktop { display: none; }
          .mobile-header { display: flex; }
          .mobile-overlay { display: block; }
          .main-content {
            margin-left: 0;
            padding: 16px;
            padding-top: 72px;
          }
        }
      `}</style>
    </div>
  )
}
